import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import Header from './components/Header.jsx';
import Sidebar from './components/Sidebar.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Trades from './pages/Trades.jsx';
import TradesList from './pages/TradesList.jsx';
import Settings from './pages/Settings.jsx';
import Wallet from './pages/Wallet.jsx';
import Analytics from './pages/Analytics.jsx';
import Login from './components/Login.jsx';

import { supabase } from './lib/supabaseClient';
import { api } from './lib/api';
import { rowToPosition, rowToSettings, rowToWatch, rowToBotEvent } from './lib/mappers';

export default function App() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const [positions, setPositions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [botEvents, setBotEvents] = useState([]); // bot signals blocked before a fill (bot_events)
  const [wallet, setWallet] = useState(null); // { pubkey, balance } from the engine
  const [solPrice, setSolPrice] = useState(null); // { price, change24h } from the engine
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [lastSync, setLastSync] = useState(Date.now());

  // ---- Auth session ----
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const isAuthed = !!session;

  // ---- Wallet info (poll the engine for pubkey + live balance) ----
  const refreshWallet = useCallback(async () => {
    try {
      const w = await api.getWallet();
      setWallet(w && w.pubkey ? w : null);
    } catch (e) {
      console.error('wallet fetch failed', e);
    }
  }, []);

  // ---- Initial load + realtime subscriptions (only while authed) ----
  useEffect(() => {
    if (!isAuthed) return;
    let active = true;

    const loadAll = async () => {
      const [pos, set, watch, events] = await Promise.all([
        supabase.from('positions').select('*').order('created_at', { ascending: false }),
        supabase.from('app_settings').select('*').eq('id', 1).single(),
        supabase.from('watchlist').select('*'),
        supabase.from('bot_events').select('*').order('created_at', { ascending: false }).limit(50)
      ]);
      if (!active) return;
      if (pos.data) setPositions(pos.data.map(rowToPosition));
      if (set.data) setSettings(rowToSettings(set.data));
      if (watch.data) setWatchlist(watch.data.map(rowToWatch));
      if (events.data) setBotEvents(events.data.map(rowToBotEvent));
      setLastSync(Date.now());
    };
    loadAll();

    // supabase-js v2 does NOT automatically rejoin a channel after CHANNEL_ERROR/TIMED_OUT/CLOSED
    // (only the underlying socket reconnects with backoff) -- an unhandled channel error silently
    // kills live updates for every table on this channel until the page is refreshed and a fresh
    // channel is created. Observed live: Realtime server logs showed transport connections sitting
    // with zero joined channels, eventually reaped. Track status + rejoin on failure so a transient
    // drop recovers on its own instead of requiring a manual refresh.
    let channel = null;
    let retryTimer = null;

    const subscribeChannel = () => {
      channel = supabase
        .channel('solsnipe')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'positions' }, () => {
          supabase.from('positions').select('*').order('created_at', { ascending: false }).then(({ data }) => {
            if (active && data) { setPositions(data.map(rowToPosition)); setLastSync(Date.now()); }
          });
          // A position changing (buy/sell fill) is the only time the wallet balance actually moves --
          // refresh it here instead of only on the blind interval below, so it updates promptly.
          refreshWallet();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'app_settings' }, (payload) => {
          if (active && payload.new) setSettings(rowToSettings(payload.new));
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'watchlist' }, () => {
          supabase.from('watchlist').select('*').then(({ data }) => { if (active && data) setWatchlist(data.map(rowToWatch)); });
        })
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'bot_events' }, (payload) => {
          if (active && payload.new) setBotEvents((prev) => [rowToBotEvent(payload.new), ...prev].slice(0, 50));
        })
        .subscribe((status, err) => {
          if (!active) return;
          if (status === 'SUBSCRIBED') {
            console.log('realtime: subscribed');
            // A fresh join can miss whatever changed while the channel was down -- resync once.
            loadAll();
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            console.warn('realtime: channel', status, err || '', '-- rejoining in 3s');
            supabase.removeChannel(channel);
            retryTimer = setTimeout(() => { if (active) subscribeChannel(); }, 3000);
          }
        });
    };
    subscribeChannel();

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (channel) supabase.removeChannel(channel);
    };
  }, [isAuthed, refreshWallet]);

  // Fallback poll -- catches anything the realtime-triggered refresh above might miss (e.g. a
  // withdrawal, which doesn't touch the positions table). Balance rarely changes outside of a
  // trade, so this is a safety net, not the primary refresh path anymore.
  useEffect(() => {
    if (!isAuthed) return;
    refreshWallet();
    const id = setInterval(refreshWallet, 60000);
    return () => clearInterval(id);
  }, [isAuthed, refreshWallet]);

  // ---- SOL price + 24h change (poll the engine; sidebar widget) ----
  const refreshSolPrice = useCallback(async () => {
    try {
      const p = await api.getSolPrice();
      if (p && p.price != null) setSolPrice(p);
    } catch (e) {
      console.error('sol price fetch failed', e);
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    refreshSolPrice();
    const id = setInterval(refreshSolPrice, 30000);
    return () => clearInterval(id);
  }, [isAuthed, refreshSolPrice]);

  // ---- Deterministic 2s positions refresh (engine writes prices every 2s) ----
  const refetchPositions = useCallback(async () => {
    const { data } = await supabase.from('positions').select('*').order('created_at', { ascending: false });
    if (data) { setPositions(data.map(rowToPosition)); setLastSync(Date.now()); }
  }, []);

  const hasOpenPositions = positions.some((p) => ['ACTIVE', 'PENDING', 'EXECUTING_BUY', 'CLOSING'].includes(p.status));
  useEffect(() => {
    if (!isAuthed || !hasOpenPositions) return; // idle: rely on Realtime for new inserts
    const id = setInterval(() => { refetchPositions(); }, 2000);
    return () => clearInterval(id);
  }, [isAuthed, hasOpenPositions, refetchPositions]);

  // ---- Command handlers (all go to the engine API) ----
  const tradingMode = settings?.tradingMode || 'PAPER';
  const setTradingMode = useCallback(async (mode) => {
    setSettings((prev) => (prev ? { ...prev, tradingMode: mode } : prev));
    try { await api.saveSettings({ tradingMode: mode }); } catch (e) { console.error(e); }
  }, []);

  const handlePlaceOrder = useCallback(async (order) => { await api.placeOrder(order); }, []);
  const handleExecuteSell = useCallback(async (id, percentage = 100, reason = 'Manual') => {
    await api.sellPosition(id, percentage, reason);
  }, []);
  const handleUpdatePosition = useCallback(async (id, updates) => { await api.updatePosition(id, updates); }, []);

  // Deletes: Supabase Realtime doesn't emit DELETE events to the client, so update
  // local state optimistically once the engine confirms the removal.
  const handleDeletePosition = useCallback(async (id) => {
    try {
      await api.deletePosition(id);
      setPositions((prev) => prev.filter((p) => p.id !== id));
    } catch (e) { console.error('delete position failed', e); }
  }, []);
  const handleClearClosedPositions = useCallback(async () => {
    try {
      await api.clearClosed();
      setPositions((prev) => prev.filter((p) => p.status !== 'CLOSED'));
    } catch (e) { console.error('clear closed failed', e); }
  }, []);
  const handleForceClose = useCallback(async (id) => {
    try {
      await api.forceClose(id);
      setPositions((prev) => prev.map((p) => (p.id === id ? { ...p, status: 'CLOSED', sellReason: 'Force-closed (manual)' } : p)));
    } catch (e) { console.error('force close failed', e); }
  }, []);

  const handleAddToWatchlist = useCallback(async (token) => {
    await api.addWatch(token.address || token, token?.strategy, token?.timeframe);
  }, []);
  const handleRemoveFromWatchlist = useCallback(async (address) => {
    try {
      await api.removeWatch(address);
      setWatchlist((prev) => prev.filter((t) => t.address !== address));
    } catch (e) { console.error('remove watchlist failed', e); }
  }, []);

  const handleUpdateWatch = useCallback(async (address, strategy, timeframe) => {
    // Optimistic local update; Realtime reconciles from the watchlist table.
    setWatchlist((prev) => prev.map((t) => (t.address === address ? { ...t, strategy: strategy || null, timeframe: timeframe || null } : t)));
    try { await api.updateWatch(address, strategy, timeframe); } catch (e) { console.error('update watchlist failed', e); }
  }, []);

  const handleSaveSettings = useCallback(async (next) => {
    setSettings(next);
    await api.saveSettings(next);
  }, []);

  const isBotActive = !!settings?.botActive;
  const setIsBotActive = useCallback(async (val) => {
    const v = typeof val === 'function' ? val(isBotActive) : val;
    setSettings((prev) => (prev ? { ...prev, botActive: v } : prev));
    try { await api.saveSettings({ botActive: v }); } catch (e) { console.error(e); }
  }, [isBotActive]);

  // Scope positions (and history) to the active mode — LIVE shows only LIVE, PAPER only PAPER.
  const visiblePositions = positions.filter((p) => p.mode === tradingMode);

  if (!authReady) {
    return <div className="min-h-screen bg-[#0B0E14]" />;
  }
  if (!isAuthed) {
    return <Login />;
  }

  return (
    <BrowserRouter>
      <div className="flex h-screen bg-[#0B0E14] text-slate-200 overflow-hidden">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} solPrice={solPrice} />

        <div className="flex-1 flex flex-col min-w-0 h-full">
          <Header
            toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
            tradingMode={tradingMode}
            setTradingMode={setTradingMode}
            wallet={wallet}
            onSignOut={() => supabase.auth.signOut()}
          />

          <main className="flex-1 overflow-hidden p-4 sm:p-6 lg:p-8">
            <Routes>
              <Route path="/" element={
                <Dashboard
                  positions={visiblePositions}
                  onPlaceOrder={handlePlaceOrder}
                  onManualSell={handleExecuteSell}
                  onUpdatePosition={handleUpdatePosition}
                  onDeletePosition={handleDeletePosition}
                  onForceClose={handleForceClose}
                  onClearClosedPositions={handleClearClosedPositions}
                  defaultSettings={settings}
                  lastSync={lastSync}
                  tradingMode={tradingMode}
                  wallet={wallet}
                  watchlist={watchlist}
                  onAddToWatchlist={handleAddToWatchlist}
                  onRemoveFromWatchlist={handleRemoveFromWatchlist}
                  onUpdateWatch={handleUpdateWatch}
                  isBotActive={isBotActive}
                  setIsBotActive={setIsBotActive}
                  botEvents={botEvents}
                />
              } />
              <Route path="/analytics" element={<Analytics defaultTimeframe={settings?.botTimeframe} strategy={settings?.strategy} signalQuality={settings?.signalQualityEnabled} />} />
              <Route path="/trades" element={<Trades positions={visiblePositions} onDeletePosition={handleDeletePosition} />} />
              <Route path="/trades-list" element={<TradesList positions={visiblePositions} />} />
              <Route path="/wallet" element={<Wallet wallet={wallet} onWalletChange={refreshWallet} />} />
              <Route path="/settings" element={<Settings settings={settings} onSaveSettings={handleSaveSettings} />} />
            </Routes>
          </main>
        </div>
      </div>
    </BrowserRouter>
  );
}
